import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { merge } from 'lodash';
import { UserType } from 'api/models';
import { PartnerAPI } from 'api/partner';
import { GENERAL_API_STALE } from 'config/query-config';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import { BaseMembersQueryOptions, ALL_USERS_QUERY_KEY, ALL_MEMBERS_QUERY_KEY } from 'queries/members';
export const ALL_PARTNER_COMPANIES_KEY = [
    'companies',
    'partner'
];
export const useGetAllPartnerCompanies = (queryOptions = {}, partnerType)=>{
    const isCurrentUserPartner = useIsCurrentUserPartner();
    const queryResult = useQuery(partnerType ? [
        ...ALL_PARTNER_COMPANIES_KEY,
        partnerType
    ] : ALL_PARTNER_COMPANIES_KEY, ()=>PartnerAPI.getAllPartnerCompanies(partnerType), {
        enabled: !isCurrentUserPartner,
        refetchOnMount: true,
        staleTime: GENERAL_API_STALE,
        ...queryOptions
    });
    const loadingState = !isCurrentUserPartner ? queryResult.isLoading : queryResult.isInitialLoading;
    return {
        ...queryResult,
        isLoading: loadingState
    };
};
export const useGetAllActivePartnerCompanies = (queryOptions = {}, partnerType)=>useGetAllPartnerCompanies({
        select: (partnerCompanies)=>partnerCompanies.filter(({ active })=>active === true),
        ...queryOptions
    }, partnerType);
export const getPartnerCompanyQueryKey = (companyId)=>[
        'companies',
        'partner',
        companyId
    ];
export const useGetPartnerCompany = (companyId, queryOptions = {})=>{
    const queryClient = useQueryClient();
    return useQuery(getPartnerCompanyQueryKey(companyId), ()=>PartnerAPI.getPartnerCompany(companyId), {
        refetchOnMount: true,
        onSuccess: (data)=>{
            const partnerCompanies = queryClient.getQueryData(ALL_PARTNER_COMPANIES_KEY);
            if (typeof partnerCompanies !== 'undefined') {
                queryClient.setQueryData(ALL_PARTNER_COMPANIES_KEY, (previousState)=>(previousState !== null && previousState !== void 0 ? previousState : []).map((x)=>x.companyId === companyId ? merge(x, data) : x));
            }
        },
        ...queryOptions
    });
};
const ALL_PARTNER_COMPANIES_PERMISSION_KEY = [
    'companies',
    'partner',
    'permissions'
];
export const useGetAllPartnerPermissions = (queryOptions = {})=>useQuery(ALL_PARTNER_COMPANIES_PERMISSION_KEY, ()=>PartnerAPI.getAllPartnerPermissions(), {
        refetchOnMount: true,
        ...queryOptions
    });
export const useCreatePartnerCompanyMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>PartnerAPI.createPartnerCompany(payload), {
        onSuccess: (createdCompany)=>{
            queryClient.setQueryData([
                ...ALL_PARTNER_COMPANIES_KEY,
                createdCompany.companyType
            ], (prev)=>[
                    ...prev !== null && prev !== void 0 ? prev : [],
                    createdCompany
                ]);
            queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
            queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
        }
    });
};
export const useUpdatePartnerCompanyMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>PartnerAPI.updatePartnerCompany(payload), {
        onSuccess: (company)=>{
            queryClient.setQueryData(ALL_PARTNER_COMPANIES_KEY, (prev)=>(prev !== null && prev !== void 0 ? prev : []).map((x)=>x.companyId === company.companyId ? {
                        ...x,
                        ...company
                    } : x));
        }
    });
};
export const useDeletePartnerCompanyMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>PartnerAPI.deletePartnerCompany(payload), {
        onSuccess: (_, company)=>{
            queryClient.setQueryData(ALL_PARTNER_COMPANIES_KEY, (prev)=>(prev !== null && prev !== void 0 ? prev : []).filter((x)=>x.companyId !== company.companyId));
        }
    });
};
export const getAlluserInPartnerQueryKey = (companyId)=>[
        'partner',
        companyId,
        'users'
    ];
export const useAllUserInPartnerCompanyQuery = (companyId, queryOptions = {})=>useQuery(getAlluserInPartnerQueryKey(companyId), ()=>PartnerAPI.getAllUserInPartnerCompany(companyId), {
        refetchOnMount: true,
        ...queryOptions
    });
export const useAllPartnerCompanyMembers = (companyId, { queryKey, queryFn, staleTime, select, ...restOptions } = {})=>{
    return useQuery({
        ...BaseMembersQueryOptions,
        select: ({ users, members, ...rest })=>{
            const result = {
                ...rest,
                users: (users !== null && users !== void 0 ? users : []).filter(({ userType, company: { companyId: userCompanyId } })=>Boolean(userType === UserType.CustomerPartner || userType === UserType.Partner) && companyId === userCompanyId),
                members: (members !== null && members !== void 0 ? members : []).filter(({ user: { userType, company: { companyId: userCompanyId } } })=>Boolean(userType === UserType.CustomerPartner || userType === UserType.Partner) && companyId === userCompanyId)
            };
            return result;
        },
        refetchOnMount: true,
        ...restOptions
    });
};
